// src/components/SimpleFormComponents/SimpleBirthdate.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { DatePicker, Tooltip, Form, theme } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import { useMemo } from 'react';
import SimpleFormItem from './SimpleFormItem';
import { getDateFormatByLocale } from 'utils/helpers';
import translateX from 'utils/translateX';
import useFormElement from 'hooks/useFormElement';
import { useSimpleFormContext } from './SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import dayjs from 'dayjs';
import { getAgeDisplay } from 'utils/date';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleBirthdate = ({
  name = 'birthdate',
  subdocumentField,
  format: formatProp,
  showTime = false,
  className = '',
  tooltipLabel, // if not provided, will be derived from entityType
  disabled,
  entityType = 'person', // 'person' shows 'age', 'company' shows 'years'
  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // Watch language changes to update date format
  const language = useZustand('theme', 'language');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, form, disabledFields } = useSimpleFormContext();
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;
  const { token } = theme.useToken();

  // watch value to keep label in sync on type/select/blur/fill
  const watchedValue = Form.useWatch(fullFieldName, form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Recalculate format when language changes
  const format = useMemo(() => {
    if (formatProp) return formatProp;
    const currentLocale = dayjs.locale();
    return getDateFormatByLocale(currentLocale);
  }, [formatProp, language]); // Re-compute when language changes

  // Derive tooltip label from entityType if not explicitly provided
  // 'person' -> 'age' (idade), 'company' -> 'years' (ano(s))
  const resolvedTooltipLabel =
    tooltipLabel ?? translateX(entityType === 'company' ? 'years' : 'age');

  const { visible, label } = getAgeDisplay(watchedValue, entityType === 'person');
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Visual standardization: use neutral/currentColor just like our other suffix icons
  const suffixIcon = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Tooltip title={resolvedTooltipLabel}>
        <span
          style={{
            display: visible ? 'inline-flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 22,
            height: 20,
            padding: '0 6px',
            borderRadius: 10,
            fontSize: 12,
            lineHeight: '18px',
            // use Ant Design theme tokens for light/dark mode support
            color: isDisabled ? token.colorTextDisabled : token.colorTextSecondary,
            backgroundColor: 'transparent',
            border: '1px solid currentColor',
          }}
        >
          {label}
        </span>
      </Tooltip>
      <CalendarOutlined />
    </span>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <SimpleFormItem {...props} name={fullFieldName} elementProps={elementProps} isDatePicker={true}>
      <DatePicker
        ref={elementRef}
        id={flattenFieldName}
        className={`w-100 no-border-radius ${className}`}
        format={format}
        showTime={showTime}
        disabled={isDisabled}
        suffixIcon={suffixIcon}
        previewValue={false}
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleBirthdate;

// *******************************************************************************
// HELPERS
// *******************************************************************************
